import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import LogoGKPY from "../components/LogoGKPY";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { api, errMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import SearchableSelect from "../components/SearchableSelect";
import { bulanOpts, tahunOpts } from "./PresensiListPage";
import { toast } from "sonner";
import { isoToDisplay } from "../components/DateInput";

export default function PresensiRekapPage() {
  const navigate = useNavigate();
  const now = new Date();
  const [opts, setOpts] = useState({ jenis_ibadah: [], komisi: [] });
  const [bulan, setBulan] = useState(String(now.getMonth() + 1));
  const [tahun, setTahun] = useState(String(now.getFullYear()));
  const [jenis, setJenis] = useState("");
  const [komisi, setKomisi] = useState("");
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/presensi/options").then((r) => setOpts(r.data)).catch(() => {}); }, []);
  const load = useCallback(async () => {
    const params = { bulan, tahun };
    if (jenis) params.jenis_ibadah = jenis;
    if (komisi) params.komisi = komisi;
    try { const r = await api.get("/presensi/rekap", { params }); setD(r.data); } catch (e) { toast.error(errMsg(e)); }
  }, [bulan, tahun, jenis, komisi]);
  useEffect(() => { load(); }, [load]);

  const chart = (d?.per_sesi || []).map((s) => ({ name: `${s.tanggal.slice(8)}/${s.tanggal.slice(5, 7)}${s.komisi ? " " + s.komisi.replace("Komisi ", "") : ""}`, Hadir: s.hadir, "Tidak Hadir": s.tidak_hadir, "Belum Dicatat": s.belum_dicatat }));

  return (
    <div className="max-w-4xl mx-auto space-y-5 print-area" data-testid="rekap-page">
      <div className="flex items-center gap-3 no-print">
        <Button variant="ghost" size="icon" className="rounded-xl" onClick={() => navigate("/presensi")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 flex-1">Rekap Presensi Bulanan</h1>
        <Button variant="outline" className="rounded-xl min-h-[44px]" onClick={() => window.print()} data-testid="print-btn"><Printer className="h-4 w-4 mr-2" /> Cetak</Button>
      </div>
      <Card className="p-4 sm:p-5 rounded-2xl flex items-center gap-4 print-header" data-testid="rekap-header">
        <LogoGKPY className="h-16 sm:h-20" testId="logo-gkpy-rekap" />
        <div className="min-w-0">
          <div className="text-xs font-semibold tracking-[0.2em] text-[hsl(224,64%,33%)] uppercase">SIAP GKPY</div>
          <div className="text-base sm:text-xl font-bold text-slate-900 uppercase tracking-tight leading-tight">Rekapitulasi Presensi Jemaat</div>
          <div className="text-xs sm:text-sm text-slate-500 mt-0.5" data-testid="rekap-periode">{bulanOpts[Number(bulan) - 1]?.label} {tahun}{jenis ? ` · ${jenis}` : ""}{komisi ? ` · ${komisi}` : ""}</div>
        </div>
      </Card>
      <Card className="p-3 rounded-2xl grid grid-cols-2 sm:grid-cols-4 gap-2 no-print">
        <SearchableSelect options={bulanOpts} value={bulanOpts[Number(bulan) - 1]?.label || ""} onChange={setBulan} placeholder="Bulan" testId="rekap-bulan" allowClear={false} />
        <SearchableSelect options={tahunOpts()} value={tahun} onChange={setTahun} placeholder="Tahun" testId="rekap-tahun" allowClear={false} />
        <SearchableSelect options={opts.jenis_ibadah} value={jenis} onChange={setJenis} placeholder="Semua jenis" testId="rekap-jenis" />
        <SearchableSelect options={opts.komisi} value={komisi} onChange={setKomisi} placeholder="Semua komisi" testId="rekap-komisi" />
      </Card>
      {d && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <K label="Total Sesi" v={d.total_sesi} testId="rekap-total-sesi" />
            <K label="Hadir" v={d.total_kehadiran} cls="text-emerald-700" testId="rekap-hadir" />
            <K label="Tidak Hadir" v={d.total_tidak_hadir} cls="text-red-600" testId="rekap-tidak-hadir" />
            <K label="Belum Dicatat" v={d.total_belum_dicatat} cls="text-slate-500" testId="rekap-belum" />
            <K label="% Kehadiran" v={`${d.persentase_kehadiran}%`} cls="text-[hsl(224,64%,33%)]" testId="rekap-persen" />
          </div>
          <Card className="p-4 rounded-2xl" data-testid="rekap-chart">
            <h3 className="font-semibold text-slate-800 mb-3">Kehadiran per Sesi</h3>
            {chart.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">Tidak ada sesi pada periode ini.</p> : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chart} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Hadir" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="Tidak Hadir" stackId="a" fill="#ef4444" />
                    <Bar dataKey="Belum Dicatat" stackId="a" fill="#cbd5e1" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
          <Card className="rounded-2xl overflow-hidden">
            <div className="divide-y divide-slate-100" data-testid="rekap-sesi-list">
              {d.per_sesi.map((s) => (
                <button key={s.sesi_id} className="w-full p-3 flex items-center justify-between gap-3 text-left hover:bg-slate-50" onClick={() => navigate(`/presensi/${s.sesi_id}`)}>
                  <div><div className="font-semibold text-slate-900 text-sm">{s.label}</div><div className="text-xs text-slate-500">{isoToDisplay(s.tanggal)}{s.komisi ? ` · ${s.jenis_ibadah}` : ""}</div></div>
                  <div className="text-xs text-right"><span className="text-emerald-700 font-semibold">{s.hadir} H</span> · <span className="text-red-600 font-semibold">{s.tidak_hadir} TH</span> · <span className="text-slate-500">{s.belum_dicatat} B</span><div className="font-bold text-slate-800">{s.persentase_kehadiran}%</div></div>
                </button>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

const K = ({ label, v, cls = "text-slate-900", testId }) => (<Card className="p-3 sm:p-4 rounded-2xl"><div className={`text-xl sm:text-2xl font-bold ${cls}`} data-testid={testId}>{v}</div><div className="text-xs text-slate-500">{label}</div></Card>);
